import { FT_TO_M } from '../util/units.js'

/**
 * Where the weather is, as plain numbers.
 *
 * Separated from `scene/Clouds.js` for the same reason `world/deckSpots.js` is
 * separated from `scene/CarrierModel.js`: the placement has properties that are
 * worth checking, the rendering needs a GPU, and there is no reason the first
 * should be hostage to the second. Nothing in here imports three.js.
 *
 * THE CEILING IS NOT A STYLE CHOICE
 * CASE 1 is defined by the weather: it requires a ceiling of at least 3,000 ft
 * and five miles of visibility. Below that you are flying CASE 2 or CASE 3,
 * which is a different recovery entirely — a marshal stack, a timed approach, a
 * controlled descent on instruments. So a cloud base is not decoration in this
 * particular simulator; get it wrong and the sky contradicts the procedure the
 * whole thing is teaching.
 */

/** Cloud base, feet. The CASE 1 minimum is 3,000; this leaves margin. */
export const CLOUD_BASE_FT = 3500

/** The hard floor. Below this it is not a CASE 1 day. */
export const CASE1_MIN_CEILING_FT = 3000

/** How far out the layer is scattered, metres. */
export const FIELD_RADIUS_M = 26000

/** Deterministic pseudo-random, so every run gets the same sky. */
export function rng(seed) {
  let s = seed >>> 0
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0
    return s / 4294967296
  }
}

/**
 * Where every cumulus goes.
 *
 * The two properties that matter are both invisible until you look up at the
 * wrong moment: the base has to clear 3,000 ft, and SOMETHING has to be close
 * enough overhead to be worth looking at in the break. Both were wrong at
 * first — bases down at 271 ft, nearest cloud 16.9 km out — and neither is the
 * kind of thing you notice by rendering a frame and squinting at it.
 *
 * @returns {Array<{x:number,y:number,z:number,w:number,h:number}>} metres;
 *          `y` is the sprite CENTRE, not its base
 */
export function cumulusField(clumps = 20) {
  const rand = rng(0x5eed)
  const out = []

  // Clumped rather than evenly spread: real scattered cumulus comes in groups
  // with clear lanes between them, and an even scatter looks like wallpaper.
  // Fewer on a phone. The first four are the ones overhead in the break, so
  // they are kept whatever the count — thinning the sky should take clouds off
  // the horizon, not the one place you are asked to look.
  const CLUMPS = Math.max(4, clumps)
  for (let c = 0; c < CLUMPS; c++) {
    const a = rand() * Math.PI * 2
    // The first four clumps are placed deliberately close. `sqrt` of a uniform
    // gives an even areal density, which is right for the field as a whole and
    // wrong near the middle of it: the nearest of twenty then lands about six
    // kilometres out — seven degrees above the horizon, so still nothing
    // overhead, and "look up in the break" is the case this exists for.
    const r =
      c < 4
        ? (0.03 + 0.09 * rand()) * FIELD_RADIUS_M
        : (0.02 + 0.98 * Math.sqrt(rand())) * FIELD_RADIUS_M
    const cx = Math.cos(a) * r
    const cz = Math.sin(a) * r

    const perClump = 3 + Math.floor(rand() * 4)
    for (let i = 0; i < perClump; i++) {
      const spread = 900 + rand() * 2200
      const size = 900 + rand() * 1700
      const h = size * (0.62 + rand() * 0.3)
      out.push({
        x: cx + (rand() - 0.5) * spread,
        // A sprite's POSITION is its centre, so a cloud placed AT the base
        // altitude hangs half its own height below it. With sprites up to
        // 2,200 m tall that put bases as low as 271 ft — under the ceiling that
        // makes this a CASE 1 day at all. Hence the `+ h / 2`.
        y: (CLOUD_BASE_FT + rand() * 1400) * FT_TO_M + h / 2,
        z: cz + (rand() - 0.5) * spread,
        w: size * (1.3 + rand() * 0.6),
        h,
      })
    }
  }
  return out
}

/** The high sheet: position and size only, no materials. */
export function cirrusField() {
  // Its own seed rather than a continuation of the cumulus stream, so that
  // adding one cumulus does not silently reshuffle the entire sheet.
  const rand = rng(0xc177)
  const out = []
  for (let i = 0; i < 26; i++) {
    const a = rand() * Math.PI * 2
    const r = (0.05 + 0.95 * Math.sqrt(rand())) * FIELD_RADIUS_M * 0.8
    out.push({
      x: Math.cos(a) * r,
      y: (22000 + rand() * 6000) * FT_TO_M,
      z: Math.sin(a) * r,
      w: 6000 + rand() * 7000,
      h: 700 + rand() * 700,
    })
  }
  return out
}

/** Lowest cloud base in a field, feet. */
export function lowestBaseFt(field) {
  let lowest = Infinity
  for (const p of field) lowest = Math.min(lowest, (p.y - p.h / 2) / FT_TO_M)
  return lowest
}

/** Range of the nearest cloud, metres. If this is large, the sky is a ring. */
export function nearestCloudM(field) {
  let nearest = Infinity
  for (const p of field) nearest = Math.min(nearest, Math.hypot(p.x, p.z))
  return nearest
}
